import { zustandImmerMiddleware } from '@sketchnote/shared-components';
import { useBoardService, useBoardStore } from 'src/features/boards';
import { useKanbanStore } from 'src/features/kanban';
import { deleteTask, updateTaskProperties } from 'src/features/tasks/api/useTasksService';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import {
  addItemToSprint,
  assignEpic,
  assignTaskToBacklog,
  assignTaskToSprint,
  completeSprint,
  createBacklogItem,
  createEpic,
  createSprint,
  deleteEpic,
  deleteSprintById,
  editSprint,
  fetchEpics,
  getBacklogsAndSprints,
  startSprint,
} from '../api/useAgileService';

export type SprintTask = {
  agile: boolean;
  assignee: string[];
  attachments: string[];
  board: string;
  column: string;
  completed: boolean;
  completedSubTaskTotal: number;
  estimates: any[];
  fileRef: string;
  hide: boolean;
  isSubtask: boolean;
  name: any[];
  description?: any[];
  number: number;
  organisation: string;
  sprint: string;
  status: string;
  subtaskCount: number;
  subtaskIndex: number;
  type: string;
  _id: string;
  storyPoint?: number;
  start_date?: string;
  end_date?: string;
  epicRef?: string;
};

export type Sprint = {
  board?: string;
  fileRef?: string;
  isSprintRunning: number;
  items: SprintTask[];
  organization?: string;
  owner?: string;
  title: string;
  totalStoryPoints: number;
  _id: string;
  description: string;
  sprintStartDate?: string;
  sprintEndDate?: string;
};

export type ScrumColumn = {
  board: string;
  color: string;
  items: string[];
  name: string;
  type: 'not_started' | 'in_progress' | 'completed';
  _id: string;
};

export const useAgileStore = create<
  AgileStore,
  [['zustand/devtools', AgileStore], ['zustand/immer', AgileStore]]
>(
  devtools(
    zustandImmerMiddleware((set, get) => ({
      backlogTasks: [],
      sprints: [],
      epics: [],
      columns: [],
      isEpic: false,
      isLoading: false,
      currentSelectedSprint: '',
      selectedView: 'sprints',
      fileId: '',
      showAddSprintTask: {},
      activeSprints: [],
      boardId: '',
      currentEpicItems: [],
      analyticsData: [],
      selectedSprintIds: [],
      setSelectedSprintIds: (sprints) => {
        set(() => ({
          selectedSprintIds: sprints,
        }));
      },
      setEpic: (status) => {
        set(() => ({
          isEpic: status,
        }));
      },
      setActiveSprints: (sprints) => {
        set(() => ({
          activeSprints: sprints,
        }));
      },
      setBacklogTasks: (backlogTasks) => {
        set(() => ({
          backlogTasks,
        }));
      },
      setSprints: (sprints) => {
        set(() => ({
          sprints,
        }));
      },
      setLoading: (status) => {
        set(() => ({
          isLoading: status,
        }));
      },
      setFileId: (id: string) => {
        set(() => ({
          fileId: id,
        }));
      },
      setAnalyticsData: (data: any) => {
        set(() => ({
          analyticsData: data,
        }));
      },
      setshowAddSprintTask: (id: string, value: boolean) => {
        set((s) => {
          const newSprintTask = { ...s.showAddSprintTask };
          newSprintTask[id] = value;
          return {
            showAddSprintTask: newSprintTask,
          };
        });
      },
      fetchBacklogsAndSprints: async (id: string) => {
        const filters = useBoardStore.getState().filters;
        const data = await getBacklogsAndSprints(id, filters);
        set(() => ({
          backlogTasks: [...data[0].backlogs].filter((item) => item.type !== 'epic'),
          sprints: data[1].sprints,
          columns: data[2].columns,
        }));
      },
      setSelectedView: (status) => {
        set(() => ({
          selectedView: status,
        }));
      },
      setCurrentSelectedSprint: (selectedSprint) => {
        set(() => ({
          currentSelectedSprint: selectedSprint,
        }));
      },

      addTaskToBacklog: async (task) => {
        set((state) => ({
          backlogTasks: [...state.backlogTasks, task],
        }));
        const { fileId, fetchBacklogsAndSprints } = get();

        const response = await createBacklogItem(task.name, fileId);
        if (response) {
          fetchBacklogsAndSprints(fileId);
        }
      },

      addTaskToSprint: async (sprintId, task) => {
        set((state) => {
          const sprintIndex = state.sprints.findIndex((sprint) => sprint._id === sprintId);
          if (sprintIndex === -1) return;

          return {
            sprints: [
              ...state.sprints.slice(0, sprintIndex),
              {
                ...state.sprints[sprintIndex],
                items: [...state.sprints[sprintIndex].items, task],
              },
              ...state.sprints.slice(sprintIndex + 1),
            ],
          };
        });
        const response = await addItemToSprint(sprintId, task.name, task.description);
        if (response) {
          const { fileId, fetchBacklogsAndSprints } = get();
          fetchBacklogsAndSprints(fileId);
        }
      },
      moveTaskToSprint: (backlogIndex, sprintIndex, task) =>
        set((state) => {
          // Remove task from backlog
          const newBacklogTasks = [
            ...state.backlogTasks.slice(0, backlogIndex),
            ...state.backlogTasks.slice(backlogIndex + 1),
          ];

          // Add task to sprint
          const newSprints = [
            ...state.sprints.slice(0, sprintIndex),
            {
              ...state.sprints[sprintIndex],
              items: [...state.sprints[sprintIndex].items, task],
            },
            ...state.sprints.slice(sprintIndex + 1),
          ];

          return {
            backlogTasks: newBacklogTasks,
            sprints: newSprints,
          };
        }),
      addSprint: async (title, description) => {
        set((state) => ({
          sprints: [...state.sprints, { items: [], title, description }],
        }));
        const { fileId, fetchBacklogsAndSprints } = get();

        const response = await createSprint(title, description, fileId);
        if (response) {
          fetchBacklogsAndSprints(fileId);
        }
      },
      editSprint: async (payload) => {
        const { fileId, fetchBacklogsAndSprints } = get();

        const response = await editSprint(payload);
        if (response) {
          fetchBacklogsAndSprints(fileId);
        }
      },
      addEpic: async (epic: any) => {
        set((state) => ({
          epics: [...state.epics, epic],
        }));
        const { fileId, fetchEpics } = get();
        const response = await createEpic(epic.name, epic.description, epic.fileId);
        if (response) {
          fetchEpics(fileId);
          return true;
        }
        return false;
      },
      fetchEpics: async (fileId) => {
        const filters = useBoardStore.getState().filters;
        const epics = await fetchEpics(fileId, filters);
        if (epics)
          set(() => ({
            epics,
          }));
      },
      onUpdateItemField: async ({ item, keys }) => {
        const payload: any = {};
        keys.map((field) => {
          if (item[field] === undefined) {
            payload[field] = null;
          } else if (field === 'assignee') {
            payload[field] = item[field]?.map((obj) => obj.memberId);
          } else if (field === 'duplicate') {
            payload[field] = item[field]?.map((obj) => obj.memberId);
          } else if (field === 'assigner') {
            payload[field] = item.assigner?.memberId;
          } else if (field === 'assignDate') {
            if (item?.assignDate?.length && item?.assignDate?.length > 1) {
              payload.start_date = item?.assignDate?.[0];
              payload.end_date = item?.assignDate?.[1];
            } else {
              payload.end_date = item?.assignDate?.[0] || null;
              payload.start_date = null;
            }
          } else if (field === 'estimates') {
            payload[field] = item[field]?.map((i) => {
              if (i.type === 'member') {
                return Object.assign({}, i, { member: i?.member?.memberId });
              } else return i;
            });
          } else {
            payload[field] = item[field];
          }
        });
        payload.id = item._id;
        if (item._id) {
          await updateTaskProperties({ properties: payload, taskId: item._id });
        }
      },
      deleteSprint: async (sprintId) => {
        set((state) => {
          const sprintIndex = state.sprints.findIndex((sprint) => sprint._id === sprintId);
          if (sprintIndex === -1) return;
          const newSprints = [...state.sprints];
          newSprints.splice(sprintIndex, 1);

          return {
            sprints: newSprints,
          };
        });
        const response = await deleteSprintById(sprintId);

        if (response) {
          const { fileId, fetchBacklogsAndSprints } = get();
          fetchBacklogsAndSprints(fileId);
        }
      },
      startSprint: async (sprintId, start_date, end_date) => {
        set((state) => {
          const sprintIndex = state.sprints.findIndex((sprint) => sprint._id === sprintId);
          if (sprintIndex === -1) return state;

          const newSprints = [...state.sprints];
          newSprints[sprintIndex] = { ...newSprints[sprintIndex], isSprintRunning: 1 };

          return {
            sprints: newSprints,
          };
        });
        const response = await startSprint(sprintId, start_date, end_date);
        if (response) {
          const { fileId, fetchBacklogsAndSprints } = get();
          fetchBacklogsAndSprints(fileId);
        }
      },
      completeSprint: async (sprintId) => {
        set((state) => {
          const sprintIndex = state.sprints.findIndex((sprint) => sprint._id === sprintId);
          if (sprintIndex === -1) return state;

          const newSprints = [...state.sprints];
          newSprints[sprintIndex] = { ...newSprints[sprintIndex], isSprintRunning: 2 };

          return {
            sprints: newSprints,
          };
        });
        const response = await completeSprint(sprintId);
        if (response) {
          const { fileId, fetchBacklogsAndSprints } = get();
          fetchBacklogsAndSprints(fileId);
        }
      },
      setItem: (updatedTask, taskId) => {
        set(({ backlogTasks, sprints, epics }) => {
          const backlogTaskTypes = ['story', 'bug', 'task'];

          const epicIndex = epics.findIndex((item) => item._id === taskId);
          if (epicIndex !== -1) {
            if (updatedTask.type !== 'epic') {
              epics.splice(epicIndex, 1);
              if (backlogTaskTypes.includes(updatedTask.type)) backlogTasks.push(updatedTask);
            } else epics[epicIndex] = updatedTask;
          }
          const backlogIndex = backlogTasks.findIndex((item) => item._id === taskId);
          if (backlogIndex !== -1) {
            if (!backlogTaskTypes.includes(updatedTask.type)) {
              backlogTasks.splice(backlogIndex, 1);
              if (updatedTask.type === 'epic') epics.push(updatedTask);
            } else backlogTasks[backlogIndex] = updatedTask;
          }

          for (let i = 0; i < sprints.length; i++) {
            const sprint = sprints[i];
            const itemIndex = sprint.items.findIndex((item) => item._id === taskId);
            if (itemIndex !== -1) {
              sprint.items[itemIndex] = updatedTask;
            }
          }
        });
      },
      onMarkEpicAsComplete: async ({ epicId, completed }) => {
        set(({ epics }) => {
          const epicIndex = epics.findIndex((item) => item._id === epicId);
          epics[epicIndex].completed = completed;
        });
        await updateTaskProperties({ properties: { completed }, taskId: epicId });
      },
      onMarkTaskAsComplete: async ({ task, completed }) => {
        const sprintIndex = get().sprints.findIndex((sprint) => sprint._id === task.sprint);

        if (sprintIndex === -1) return;
        const itemIndex = get().sprints[sprintIndex].items.findIndex(
          (item) => item._id === task._id
        );
        if (itemIndex === -1) return;
        set(({ sprints }) => {
          sprints[sprintIndex].items[itemIndex].completed = completed;
        });
        await updateTaskProperties({ properties: { completed }, taskId: task._id });
      },
      updateSprintItem: (sprintId, task) => {
        set(({ sprints, backlogTasks, epics }) => {
          if (sprintId === 'backlog') {
            const backlogIndex = backlogTasks.findIndex((item) => item._id === task._id);
            if (backlogIndex === -1) return;
            backlogTasks[backlogIndex] = task;
            return;
          }
          if (sprintId === 'epics') {
            const epicIndex = epics.findIndex((item) => item._id === task._id);
            if (epicIndex === -1) return;
            epics[epicIndex] = task;
            return;
          }
          const sprintIndex = sprints.findIndex((sprint) => sprint._id === sprintId);
          if (sprintIndex === -1) return;
          const itemIndex = sprints[sprintIndex].items.findIndex((item) => item._id === task._id);
          if (itemIndex === -1) return;
          sprints[sprintIndex].items[itemIndex] = task;
        });
      },
      removeSprintItem: (sprintId, taskId) => {
        let task;
        set(({ sprints, backlogTasks, epics }) => {
          if (sprintId === 'backlog') {
            const backlogIndex = backlogTasks.findIndex((item) => item._id === taskId);
            task = backlogTasks.splice(backlogIndex, 1)[0];
            return;
          }
          if (sprintId === 'epics') {
            const epicIndex = epics.findIndex((item) => item._id === taskId);
            if (epicIndex === -1) return;
            task = epics.splice(epicIndex, 1)[0];
            return;
          }
          const sprintIndex = sprints.findIndex((sprint) => sprint._id === sprintId);
          if (sprintIndex === -1) return;
          const itemIndex = sprints[sprintIndex].items.findIndex((item) => item._id === taskId);
          if (itemIndex === -1) return;
          task = sprints[sprintIndex].items.splice(itemIndex, 1)[0];
        });
        return task;
      },
      addSprintItem: (sprintId, task) => {
        set(({ sprints, backlogTasks, epics }) => {
          if (sprintId === 'backlog') {
            backlogTasks.push(task);
            return;
          }
          if (sprintId === 'epics') {
            epics.push(task);
            return;
          }
          const sprintIndex = sprints.findIndex((sprint) => sprint._id === sprintId);
          if (sprintIndex === -1) return;
          sprints[sprintIndex].items.push(task);
        });
      },
      deleteSprintItem: async (sprintId, taskId) => {
        if (sprintId === 'epics') {
          const response = await deleteEpic(taskId);
          if (!response) return;
          set(({ epics }) => {
            const epicIndex = epics.findIndex((item) => item._id === taskId);
            if (epicIndex === -1) return;
            epics.splice(epicIndex, 1);
          });
        } else {
          const response = await deleteTask(taskId);
          if (!response) return;
          if (sprintId === 'backlog') {
            set(({ backlogTasks }) => {
              const backlogIndex = backlogTasks.findIndex((item) => item._id === taskId);
              if (backlogIndex === -1) return;
              backlogTasks.splice(backlogIndex, 1);
            });
          } else {
            set(({ sprints }) => {
              const sprintIndex = sprints.findIndex((sprint) => sprint._id === sprintId);
              if (sprintIndex === -1) return;
              const itemIndex = sprints[sprintIndex].items.findIndex((item) => item._id === taskId);
              if (itemIndex === -1) return;
              sprints[sprintIndex].items.splice(itemIndex, 1);
            });
          }
        }
      },
      moveSprintItem: (taskId, sourceId, destId) => {
        set(({ sprints, backlogTasks, epics }) => {
          let task;
          if (sourceId === 'backlog') {
            const backlogIndex = backlogTasks.findIndex((item) => item._id === taskId);
            task = backlogTasks.splice(backlogIndex, 1)[0];
          } else if (sourceId === 'epics') {
            const epicIndex = epics.findIndex((item) => item._id === taskId);
            if (epicIndex === -1) return;
            task = epics.splice(epicIndex, 1)[0];
          } else {
            const sourceSprintIndex = sprints.findIndex((sprint) => sprint._id === sourceId);
            if (sourceSprintIndex === -1) return;
            const itemIndex = sprints[sourceSprintIndex].items.findIndex(
              (item) => item._id === taskId
            );
            if (itemIndex === -1) return;
            task = sprints[sourceSprintIndex].items.splice(itemIndex, 1)[0];
          }
          if (destId === 'backlog') {
            backlogTasks.push(task);
            return;
          } else if (destId === 'epics') {
            epics.push(task);
            return;
          } else {
            const destSprintIndex = sprints.findIndex((sprint) => sprint._id === destId);
            if (destSprintIndex === -1) return;
            sprints[destSprintIndex] = {
              ...sprints[destSprintIndex],
              items: [...sprints[destSprintIndex].items, task],
            };
          }
        });
        if (sourceId === 'backlog' && !['backlog', 'epics'].includes(destId)) {
          assignTaskToSprint([taskId], destId);
        } else if (!['backlog', 'epics'].includes(sourceId) && destId === 'backlog') {
          assignTaskToBacklog(taskId);
        } else if (destId === 'epics') {
          get().onUpdateItemField({ item: { _id: taskId, type: 'epic' }, keys: ['type'] });
        }
      },

      search: '',
      setSearch: (search) => {
        set(() => ({
          search,
        }));
      },

      assignTaskToEpic: async (taskId, sectionId, epicId) => {
        const task = await assignEpic(taskId, epicId);
        if (!task) return;
        set((s) => {
          if (sectionId === 'backlog') {
            const taskIndex = s.backlogTasks.findIndex((item) => item._id === taskId);
            if (taskIndex === -1) return;
            s.backlogTasks[taskIndex] = task;
          } else if (sectionId === 'epics') {
            const taskIndex = s.epics.findIndex((item) => item._id === taskId);
            if (taskIndex === -1) return;
            s.epics[taskIndex] = task;
          } else {
            const sprintIndex = s.sprints.findIndex((sprint) => sprint._id === sectionId);
            if (sprintIndex === -1) return;
            const taskIndex = s.sprints[sprintIndex].items.findIndex((item) => item._id === taskId);
            if (taskIndex === -1) return;
            s.sprints[sprintIndex].items[taskIndex] = task;
          }
        });
      },

      changeSprintItemStatus: async (
        taskId,
        sprintId,
        srcStatusColumnId,
        destStatusColumnId,
        boardId
      ) => {
        set((s) => {
          if (sprintId === 'backlog') {
            const itemIndex = s.backlogTasks.findIndex((item) => item._id === taskId);
            if (itemIndex === -1) return;
            const item = s.backlogTasks[itemIndex];
            item.column = destStatusColumnId;
          } else if (sprintId === 'epics') {
            const itemIndex = s.epics.findIndex((item) => item._id === taskId);
            if (itemIndex === -1) return;
            const item = s.epics[itemIndex];
            item.column = destStatusColumnId;
          } else {
            let sprintIndex = s.sprints.findIndex((sprint) => sprint._id === sprintId);
            if (sprintIndex === -1)
              sprintIndex = s.sprints.findIndex(
                (sprint) => !!sprint.items.find((item) => item._id === taskId)
              );
            if (sprintIndex === -1) return;
            const itemIndex = s.sprints[sprintIndex].items.findIndex((item) => item._id === taskId);
            if (itemIndex === -1) return;
            const sprint = s.sprints[sprintIndex];
            const item = sprint.items[itemIndex];
            item.column = destStatusColumnId;
          }
        });
        await useKanbanStore.getState().onUpdateItemPlacement({
          boardId: boardId,
          itemId: taskId,
          destColId: destStatusColumnId,
          sourceColId: srcStatusColumnId,
          destIndex: 0,
        });
      },

      clearStore: () => {
        set(() => ({
          backlogTasks: [],
          sprints: [],
          epics: [],
          isEpic: false,
          isLoading: false,
          currentSelectedSprint: '',
          selectedView: 'sprints',
          fileId: '',
          showAddSprintTask: {},
          activeSprints: [],
          boardId: '',
          currentEpicItems: [],
          analyticsData: [],
          search: '',
        }));
      },
    }))
  )
);

interface AgileStore {
  backlogTasks: SprintTask[];
  sprints: Sprint[];
  epics: SprintTask[];
  columns: ScrumColumn[];
  isEpic: boolean;
  isLoading: boolean;
  selectedView: 'sprints' | 'board' | 'analytics' | 'epics';
  selectedSprintIds: string[];
  fileId: string;
  activeSprints: any[];
  currentEpicItems: any[];
  boardId: string;
  analyticsData: any;
  setSelectedSprintIds: (arg: string[]) => void;
  onMarkEpicAsComplete: (arg: { epicId: string; completed: boolean }) => Promise<void>;
  onMarkTaskAsComplete: (arg: { task: any; completed: boolean }) => Promise<void>;
  setActiveSprints: (arg: any) => void;
  addTaskToBacklog: (arg: any) => void;
  addSprint: (title: string, description: string) => Promise<void>;
  editSprint: (payload: any) => Promise<void>;
  addTaskToSprint: (sprintId: string, task: any) => Promise<void>;
  setAnalyticsData: (arg: any) => void;
  currentSelectedSprint: string;
  setCurrentSelectedSprint: (arg: string) => void;
  addEpic: (arg: any) => Promise<boolean>;
  setEpic: (arg: boolean) => void;
  setBacklogTasks: (arg: any) => void;
  setSprints: (arg: any) => void;
  setLoading: (arg: boolean) => void;
  deleteSprint: (arg: string) => Promise<void>;
  startSprint: (arg: string, start_date: Date, end_date: Date) => Promise<void>;
  completeSprint: (arg: string) => Promise<void>;
  setSelectedView: (arg: 'board' | 'sprints' | 'analytics' | 'epics') => void;
  fetchBacklogsAndSprints: (id: string) => Promise<void>;
  fetchEpics: (id: string) => Promise<void>;
  setFileId: (arg: string) => void;
  showAddSprintTask: any;
  setshowAddSprintTask: (sprintId: string, value: boolean) => void;
  onUpdateItemField: (arg: { item: any; keys: any[] }) => any;
  setItem: (task: any, taskId: string) => void;

  updateSprintItem: (sprintId: string, task: any) => void;
  removeSprintItem: (sprintId: string, taskId: any) => SprintTask | void;
  addSprintItem: (sprintId: string, task: any) => void;
  deleteSprintItem: (sprintId: string, taskId: any) => Promise<void>;

  moveSprintItem: (taskId: string, sourceSprintId: string, destinationSprintId: string) => void;
  search: string;
  setSearch: (arg: string) => void;

  assignTaskToEpic: (taskId: string, sectionId: string, epicId: string) => Promise<void>;

  changeSprintItemStatus: (
    taskId: string,
    sprintId: string,
    srcStatusColumnId: string,
    destStatusColumnId: string,
    boardId: string
  ) => Promise<void>;

  clearStore: () => void;
}
